import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, Mountain, Users, Calendar, FolderOpen, 
  BookOpen, Images, Bed, MessageSquare, Heart, Settings,
  LogOut, Menu, X, Search, Plus, Edit, Trash2, Upload, Save
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { LANGUAGES } from '../../contexts/LanguageContext';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { ImageUploader } from '../ImageUpload';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SIDEBAR_ITEMS = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/admin' },
  { icon: Mountain, label: 'Falesie', path: '/admin/crags' },
  { icon: Users, label: 'Team', path: '/admin/team' },
  { icon: Calendar, label: 'Eventi', path: '/admin/events' },
  { icon: FolderOpen, label: 'Progetti', path: '/admin/projects' },
  { icon: BookOpen, label: 'Blog', path: '/admin/blog' },
  { icon: Images, label: 'Gallery', path: '/admin/gallery' },
  { icon: Bed, label: 'Alloggi', path: '/admin/accommodations' },
  { icon: MessageSquare, label: 'Messaggi', path: '/admin/messages' },
  { icon: Heart, label: 'Donazioni', path: '/admin/donations' },
  { icon: Search, label: 'SEO', path: '/admin/seo' },
  { icon: Upload, label: 'Uploads', path: '/admin/uploads' },
  { icon: Settings, label: 'Impostazioni', path: '/admin/settings' },
];

function AdminSidebar({ currentPath, sidebarOpen, setSidebarOpen, logout }) {
  return (
    <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-[#0A0A0A] transform transition-transform duration-300 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
      <div className="flex items-center justify-between h-16 px-6 border-b border-gray-800">
        <Link to="/" className="text-white font-heading text-lg uppercase">CMS Admin</Link>
        <button className="lg:hidden text-white" onClick={() => setSidebarOpen(false)}><X className="w-6 h-6" /></button>
      </div>
      <nav className="p-4 space-y-1 max-h-[calc(100vh-8rem)] overflow-y-auto">
        {SIDEBAR_ITEMS.map((item) => (
          <Link key={item.path} to={item.path} className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${currentPath === item.path ? 'bg-[#FF6D00] text-white' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}>
            <item.icon className="w-5 h-5" /><span className="text-sm">{item.label}</span>
          </Link>
        ))}
      </nav>
      <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-800">
        <button onClick={logout} className="flex items-center gap-3 px-4 py-3 text-gray-400 hover:text-white w-full">
          <LogOut className="w-5 h-5" /><span className="text-sm">Logout</span>
        </button>
      </div>
    </aside>
  );
}

function TranslatedInput({ label, value, onChange, placeholder, multiline = false }) {
  const [activeLang, setActiveLang] = useState('it');
  const Component = multiline ? Textarea : Input;
  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium">{label}</label>
      <Tabs value={activeLang} onValueChange={setActiveLang}>
        <TabsList className="h-8">
          {Object.values(LANGUAGES).map(lang => (<TabsTrigger key={lang.code} value={lang.code} className="text-xs px-2 py-1">{lang.flag}</TabsTrigger>))}
        </TabsList>
        {Object.values(LANGUAGES).map(lang => (
          <TabsContent key={lang.code} value={lang.code} className="mt-2">
            <Component value={value?.[lang.code] || ''} onChange={(e) => onChange({ ...value, [lang.code]: e.target.value })} placeholder={`${placeholder} (${lang.name})`} rows={multiline ? 4 : undefined} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

// ==================== ADMIN EVENTS ====================
export function AdminEvents() {
  const { logout, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showDialog, setShowDialog] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) { navigate('/admin/login'); return; }
    fetchItems();
  }, [isAuthenticated, authLoading, navigate]);

  const fetchItems = async () => {
    try { const res = await axios.get(`${API}/events?active_only=false`); setItems(res.data); }
    catch (error) { toast.error('Errore nel caricamento'); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    try {
      if (editing.id && items.find(i => i.id === editing.id)) {
        await axios.put(`${API}/events/${editing.id}`, editing);
        toast.success('Evento aggiornato');
      } else {
        await axios.post(`${API}/events`, editing);
        toast.success('Evento creato');
      }
      setShowDialog(false); fetchItems();
    } catch (error) { toast.error('Errore nel salvataggio'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questo evento?')) return;
    try { await axios.delete(`${API}/events/${id}`); toast.success('Evento eliminato'); fetchItems(); }
    catch (error) { toast.error('Errore nell\'eliminazione'); }
  };

  const openNew = () => {
    setEditing({
      title: { it: '', en: '', fr: '', de: '', es: '' },
      description: { it: '', en: '', fr: '', de: '', es: '' },
      date: new Date().toISOString().split('T')[0],
      location: '',
      images: [],
      is_active: true,
      is_featured: false
    });
    setShowDialog(true);
  };

  if (authLoading || !isAuthenticated) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminSidebar currentPath="/admin/events" sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} logout={logout} />
      <div className="lg:ml-64">
        <header className="bg-white border-b h-16 flex items-center justify-between px-6">
          <button className="lg:hidden" onClick={() => setSidebarOpen(true)}><Menu className="w-6 h-6" /></button>
          <h1 className="font-heading text-xl uppercase">Gestione Eventi</h1>
          <Button onClick={openNew} className="btn-primary"><Plus className="w-4 h-4 mr-2" />Nuovo Evento</Button>
        </header>

        <main className="p-6">
          {loading ? (
            <div className="flex justify-center py-20"><div className="animate-spin w-8 h-8 border-4 border-[#FF6D00] border-t-transparent rounded-full" /></div>
          ) : (
            <div className="bg-white rounded-lg border overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left py-4 px-6 font-heading uppercase text-sm">Titolo</th>
                    <th className="text-left py-4 px-6 font-heading uppercase text-sm">Data</th>
                    <th className="text-left py-4 px-6 font-heading uppercase text-sm">Luogo</th>
                    <th className="text-left py-4 px-6 font-heading uppercase text-sm">Stato</th>
                    <th className="text-right py-4 px-6 font-heading uppercase text-sm">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.id} className="border-b hover:bg-gray-50">
                      <td className="py-4 px-6 font-medium">{item.title?.it || item.title?.en || '-'}</td>
                      <td className="py-4 px-6">{new Date(item.date).toLocaleDateString('it-IT')}</td>
                      <td className="py-4 px-6 text-gray-500">{item.location || '-'}</td>
                      <td className="py-4 px-6">
                        <span className={`px-2 py-1 rounded text-xs ${item.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                          {item.is_active ? 'Attivo' : 'Nascosto'}
                        </span>
                      </td>
                      <td className="py-4 px-6 text-right">
                        <div className="flex justify-end gap-2">
                          <button onClick={() => { setEditing(item); setShowDialog(true); }} className="p-2 hover:bg-gray-100 rounded"><Edit className="w-4 h-4 text-blue-600" /></button>
                          <button onClick={() => handleDelete(item.id)} className="p-2 hover:bg-gray-100 rounded"><Trash2 className="w-4 h-4 text-red-600" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </main>
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing?.id ? 'Modifica Evento' : 'Nuovo Evento'}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4 py-4">
              <TranslatedInput label="Titolo" value={editing.title} onChange={(v) => setEditing({...editing, title: v})} placeholder="Titolo evento" />
              <TranslatedInput label="Descrizione" value={editing.description} onChange={(v) => setEditing({...editing, description: v})} placeholder="Descrizione" multiline />
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium mb-2">Data</label>
                  <Input type="date" value={editing.date?.split('T')[0]} onChange={(e) => setEditing({...editing, date: e.target.value})} /></div>
                <div><label className="block text-sm font-medium mb-2">Luogo</label>
                  <Input value={editing.location} onChange={(e) => setEditing({...editing, location: e.target.value})} /></div>
              </div>
              <div><label className="block text-sm font-medium mb-2">Immagini</label>
                <ImageUploader value={editing.images} onChange={(images) => setEditing({...editing, images: images.map(i => typeof i === 'string' ? i : i.url)})} category="events" maxImages={5} /></div>
              <div className="flex gap-4">
                <label className="flex items-center gap-2"><input type="checkbox" checked={editing.is_active} onChange={(e) => setEditing({...editing, is_active: e.target.checked})} /><span className="text-sm">Attivo</span></label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={editing.is_featured} onChange={(e) => setEditing({...editing, is_featured: e.target.checked})} /><span className="text-sm">In evidenza</span></label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Annulla</Button>
            <Button onClick={handleSave} className="btn-primary"><Save className="w-4 h-4 mr-2" />Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ==================== ADMIN PROJECTS ====================
export function AdminProjects() {
  const { logout, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showDialog, setShowDialog] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) { navigate('/admin/login'); return; }
    fetchItems();
  }, [isAuthenticated, authLoading, navigate]);

  const fetchItems = async () => {
    try { const res = await axios.get(`${API}/projects?active_only=false`); setItems(res.data); }
    catch (error) { toast.error('Errore nel caricamento'); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    try {
      if (editing.id && items.find(i => i.id === editing.id)) {
        await axios.put(`${API}/projects/${editing.id}`, editing);
        toast.success('Progetto aggiornato');
      } else {
        await axios.post(`${API}/projects`, editing);
        toast.success('Progetto creato');
      }
      setShowDialog(false); fetchItems();
    } catch (error) { toast.error('Errore nel salvataggio'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questo progetto?')) return;
    try { await axios.delete(`${API}/projects/${id}`); toast.success('Progetto eliminato'); fetchItems(); }
    catch (error) { toast.error('Errore nell\'eliminazione'); }
  };

  const openNew = () => {
    setEditing({
      title: { it: '', en: '', fr: '', de: '', es: '' },
      description: { it: '', en: '', fr: '', de: '', es: '' },
      status: 'in_progress',
      images: [],
      is_active: true
    });
    setShowDialog(true);
  };

  if (authLoading || !isAuthenticated) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminSidebar currentPath="/admin/projects" sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} logout={logout} />
      <div className="lg:ml-64">
        <header className="bg-white border-b h-16 flex items-center justify-between px-6">
          <button className="lg:hidden" onClick={() => setSidebarOpen(true)}><Menu className="w-6 h-6" /></button>
          <h1 className="font-heading text-xl uppercase">Gestione Progetti</h1>
          <Button onClick={openNew} className="btn-primary"><Plus className="w-4 h-4 mr-2" />Nuovo Progetto</Button>
        </header>

        <main className="p-6">
          {loading ? (
            <div className="flex justify-center py-20"><div className="animate-spin w-8 h-8 border-4 border-[#FF6D00] border-t-transparent rounded-full" /></div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {items.map((item) => (
                <div key={item.id} className="bg-white rounded-lg border overflow-hidden">
                  <div className="aspect-video bg-gray-100">
                    {item.images?.[0] ? (<img src={item.images[0]} alt="" className="w-full h-full object-cover" />) : (<div className="w-full h-full flex items-center justify-center text-gray-400"><FolderOpen className="w-12 h-12" /></div>)}
                  </div>
                  <div className="p-4">
                    <span className={`text-xs px-2 py-1 rounded ${item.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
                      {item.status === 'completed' ? 'Completato' : 'In corso'}
                    </span>
                    <h3 className="font-heading text-lg mt-2">{item.title?.it || item.title?.en || '-'}</h3>
                    <div className="flex gap-2 mt-4">
                      <Button variant="outline" size="sm" onClick={() => { setEditing(item); setShowDialog(true); }}><Edit className="w-4 h-4" /></Button>
                      <Button variant="outline" size="sm" onClick={() => handleDelete(item.id)}><Trash2 className="w-4 h-4 text-red-600" /></Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </main>
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing?.id ? 'Modifica Progetto' : 'Nuovo Progetto'}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4 py-4">
              <TranslatedInput label="Titolo" value={editing.title} onChange={(v) => setEditing({...editing, title: v})} placeholder="Titolo progetto" />
              <TranslatedInput label="Descrizione" value={editing.description} onChange={(v) => setEditing({...editing, description: v})} placeholder="Descrizione" multiline />
              <div><label className="block text-sm font-medium mb-2">Stato</label>
                <select className="w-full border rounded px-3 py-2" value={editing.status} onChange={(e) => setEditing({...editing, status: e.target.value})}>
                  <option value="in_progress">In corso</option>
                  <option value="completed">Completato</option>
                </select></div>
              <div><label className="block text-sm font-medium mb-2">Immagini</label>
                <ImageUploader value={editing.images} onChange={(images) => setEditing({...editing, images: images.map(i => typeof i === 'string' ? i : i.url)})} category="projects" maxImages={5} /></div>
              <label className="flex items-center gap-2"><input type="checkbox" checked={editing.is_active} onChange={(e) => setEditing({...editing, is_active: e.target.checked})} /><span className="text-sm">Attivo</span></label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Annulla</Button>
            <Button onClick={handleSave} className="btn-primary"><Save className="w-4 h-4 mr-2" />Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
